import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useId } from 'react'
import { numberFmt } from '@/lib/format'
import { axisLabel, type Bucket, periodLabel } from './period'
import { niceTicks } from './ticks'

export interface TimeSeries {
  key: string
  label: string
  /** A chart colour token, e.g. var(--viz-1), in the fixed categorical order. */
  color: string
}

export interface TimePoint {
  start: string
  end: string
  [key: string]: string | number
}

interface TimeChartProps {
  title: string
  /** area for a level over time (MRR, customers); bar for counts and sums per period. */
  kind: 'area' | 'bar'
  bucket: Bucket
  points: TimePoint[]
  series: TimeSeries[]
  format?: (value: number) => string
  axisFormat?: (value: number) => string
  height?: number
}

const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 })

/**
 * One chart over time (dataviz: 2px line with a light wash, or bars with 4px rounded tops and a
 * 2px gap; hairline grid; one axis). Hovering shows the period's values; two or more series get
 * a legend; every value is also in the data table.
 */
export function TimeChart({
  title,
  kind,
  bucket,
  points,
  series,
  format = (v) => numberFmt.format(v),
  axisFormat = (v) => (Math.abs(v) >= 1000 ? compact.format(v) : numberFmt.format(v)),
  height = 180,
}: TimeChartProps) {
  const gradientId = useId()
  const value = (point: TimePoint, key: string) => Number(point[key] ?? 0)
  const ticks = niceTicks(points.flatMap((p) => series.map((s) => value(p, s.key))))

  const axes = (
    <>
      <CartesianGrid vertical={false} stroke="var(--viz-grid)" strokeWidth={1} />
      <XAxis
        dataKey="start"
        tickFormatter={(start: string) => axisLabel(bucket, start)}
        tick={{ fill: 'var(--viz-axis)', fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: 'var(--viz-grid)' }}
        minTickGap={24}
      />
      <YAxis
        width={48}
        ticks={ticks}
        domain={[ticks[0] ?? 0, ticks.at(-1) ?? 1]}
        interval={0}
        tickFormatter={axisFormat}
        tick={{ fill: 'var(--viz-axis)', fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        allowDecimals={false}
      />
      <Tooltip
        cursor={
          kind === 'area' ? { stroke: 'var(--viz-axis)', strokeWidth: 1 } : { fill: 'var(--surface-sunken)' }
        }
        content={({ active, payload }) => {
          const point = payload[0]?.payload as TimePoint | undefined
          if (!active || !point) return null
          return (
            <div className="grid gap-1 rounded-md border border-border bg-surface-raised px-3 py-2 text-xs shadow-pop">
              <span className="text-ink-subtle">{periodLabel(bucket, point.start, point.end)}</span>
              {series.map((s) => (
                <span key={s.key} className="flex items-center gap-2 text-ink-muted">
                  <span
                    className="inline-block size-2 rounded-full"
                    style={{ background: s.color }}
                    aria-hidden="true"
                  />
                  {s.label}
                  <span className="ml-auto pl-3 font-semibold text-ink tabular-nums">
                    {format(value(point, s.key))}
                  </span>
                </span>
              ))}
            </div>
          )
        }}
      />
    </>
  )

  return (
    <figure className="grid gap-2">
      <figcaption className="sr-only">{title}</figcaption>
      {series.length > 1 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted" aria-hidden="true">
          {series.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span className="inline-block size-2 rounded-full" style={{ background: s.color }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div style={{ height }} className="w-full" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          {kind === 'area' ? (
            <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={series[0]?.color} stopOpacity={0.14} />
                  <stop offset="100%" stopColor={series[0]?.color} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              {axes}
              {series.map((s) => (
                <Area
                  key={s.key}
                  // Straight between periods: a curve would suggest values that were never measured.
                  type="linear"
                  dataKey={s.key}
                  stroke={s.color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill={`url(#${gradientId})`}
                  activeDot={{ r: 4, fill: s.color, stroke: 'var(--surface)', strokeWidth: 2 }}
                  dot={
                    points.length === 1
                      ? { r: 4, fill: s.color, stroke: 'var(--surface)', strokeWidth: 2 }
                      : false
                  }
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          ) : (
            <BarChart
              data={points}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              barGap={2}
              barCategoryGap="22%"
            >
              {axes}
              {series.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  fill={s.color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      <details className="group">
        <summary className="w-fit cursor-pointer text-xs text-ink-subtle underline-offset-4 hover:text-ink hover:underline">
          Show data table
        </summary>
        <div className="mt-2 max-h-64 overflow-auto rounded-md border border-border">
          <table className="w-full text-left text-xs">
            <caption className="sr-only">{title}</caption>
            <thead className="sticky top-0 bg-surface-sunken">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold">
                  Period
                </th>
                {series.map((s) => (
                  <th key={s.key} scope="col" className="px-3 py-2 text-right font-semibold">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.start} className="border-t border-border">
                  <td className="px-3 py-1.5">{periodLabel(bucket, point.start, point.end)}</td>
                  {series.map((s) => (
                    <td key={s.key} className="px-3 py-1.5 text-right tabular-nums">
                      {format(value(point, s.key))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  )
}
